import type { DocumentType } from "@/ui/organisms/dealTypes";

import type { CoaSelectState } from "./CoaSelectPrompt";

/**
 * Whether an upload is an Operating Statement one (the only kind the CoA prompt
 * applies to). When Rent Roll upload is disabled there is no document-type
 * choice — every upload is an OS upload.
 */
export const isOperatingStatementUpload = (
    isRentRollUploadEnabled: boolean,
    documentType: Extract<
        DocumentType,
        "operating_statement" | "rent_roll"
    > | null,
): boolean =>
    isRentRollUploadEnabled ? documentType === "operating_statement" : true;

/**
 * The Base CoA — the backend default for projects created without an explicit
 * choice (bulk creation, Rent Roll sync). Keep in sync with
 * project_router.py, which treats coa_id = 1 the same way.
 */
export const BASE_COA_ID = 1;

/**
 * Single source of truth for the OS-upload CoA picker's view state.
 *
 * Mirrors proda-core-ui's `deriveCoaState`
 * (src/ui/views/ViewFileUpload/createViewFileUpload.tsx) after OS-3983: the
 * prompt exists only when the user has a real choice to make.
 * - A non-Base CoA was a positive user choice — nothing to prompt for.
 * - A locked CoA (the project already has an OS extraction) can't be changed,
 *   so prompting would only offer a doomed reassignment (409) — the extraction
 *   just proceeds.
 * - A single permitted CoA is assigned silently on Continue (`required` is
 *   false and the prompt renders nothing), matching project creation.
 *
 * The ordering is load-bearing: applicability is decided from project state
 * (`baselineStatus`, then `coaSelectionNeeded`/`coaLocked`) FIRST, and only
 * THEN does the permitted-CoA list's load/error status matter — so a slow or
 * failing CoA-list request can never surface an error for, or block, an upload
 * that doesn't need the prompt. `baselineStatus: "pending"` +
 * `availableCoAsStatus: "rejected"` is therefore deliberately "loading", not
 * "error" (pinned in deriveCoaState.test.ts).
 */
export const deriveCoaState = (params: {
    isOperatingStatement: boolean;
    baselineStatus: "pending" | "rejected" | "resolved";
    /** The project sits on the Base CoA — the only case that needs a prompt. */
    coaSelectionNeeded: boolean;
    coaLocked: boolean;
    availableCoAsStatus: "pending" | "rejected" | "resolved";
    availableCoAs: { coaId: number; name: string }[];
    selectedCoaId: number | null;
    assigning: boolean;
    onSelect: (coaId: number) => void;
    onRetry: () => void;
}): CoaSelectState => {
    if (!params.isOperatingStatement) return { kind: "not_applicable" };
    if (params.baselineStatus === "rejected") {
        return { kind: "error", onRetry: params.onRetry };
    }
    if (params.baselineStatus !== "resolved") return { kind: "loading" };
    // A non-Base CoA was a positive user choice — nothing to prompt for.
    if (!params.coaSelectionNeeded) return { kind: "not_applicable" };
    // A locked CoA can't be changed, so there is nothing to prompt for.
    if (params.coaLocked) return { kind: "not_applicable" };
    if (params.availableCoAsStatus === "rejected") {
        return { kind: "error", onRetry: params.onRetry };
    }
    if (params.availableCoAsStatus !== "resolved") return { kind: "loading" };
    return {
        kind: "ready",
        options: params.availableCoAs,
        selectedCoaId: params.selectedCoaId,
        onSelect: params.onSelect,
        onRetry: params.onRetry,
        required: params.availableCoAs.length > 1,
        assigning: params.assigning,
    };
};
